import { useEffect, useState } from "react";
import { LifeBuoy, X as CloseIcon, Check, Send } from "lucide-react";
import { supportApi } from "@/api/support";

/**
 * Botão flutuante de Suporte (canto inferior direito, em qualquer tela do app).
 * Abre um formulário; ao enviar, mostra "obrigado" com barra de contagem
 * regressiva que fecha sozinha. Os dados vão pra planilha (via backend).
 */

type Form = {
  nome: string;
  data: string;
  area: string;
  novaFeature: string;
  cenario: string;
  processoAtual: string;
  ferramentas: string;
};

const EMPTY: Form = {
  nome: "",
  data: new Date().toISOString().slice(0, 10),
  area: "",
  novaFeature: "",
  cenario: "",
  processoAtual: "",
  ferramentas: "",
};

const CLOSE_AFTER_MS = 4000;

// Rascunho automático: o que o time digita é salvo no navegador a cada tecla e
// volta ao reabrir, pra ninguém perder o texto se a janela fechar sem querer.
const DRAFT_KEY = "mohdus:support-draft";

/** Campos que contam como "conteúdo" — `data` é auto-preenchida, não conta. */
function formHasContent(f: Form): boolean {
  return (["nome", "area", "novaFeature", "cenario", "processoAtual", "ferramentas"] as const).some(
    (k) => f[k].trim() !== "",
  );
}

function loadDraft(): Form {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<Form>;
    // Mescla com EMPTY (tolera campos novos) e recupera só o que é string.
    const merged = { ...EMPTY };
    for (const k of Object.keys(EMPTY) as Array<keyof Form>) {
      if (typeof parsed[k] === "string") merged[k] = parsed[k] as string;
    }
    return merged;
  } catch {
    return EMPTY;
  }
}

function saveDraft(f: Form): void {
  try {
    if (formHasContent(f)) localStorage.setItem(DRAFT_KEY, JSON.stringify(f));
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* navegador sem localStorage (modo privativo) — segue sem rascunho */
  }
}

function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* noop */
  }
}

const inputBase: React.CSSProperties = {
  width: "100%",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 9,
  padding: "8px 11px",
  fontSize: 13.5,
  fontFamily: "var(--wf-font-body)",
  color: "var(--wf-ink)",
  background: "var(--wf-paper)",
  outline: "none",
};

const labelBase: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--wf-ink-3)",
  marginBottom: 5,
  display: "block",
};

export function SupportWidget() {
  const [open, setOpen] = useState(false);
  // Inicia do rascunho salvo no navegador — se a janela fechou sem enviar, o
  // texto volta exatamente como estava.
  const [form, setForm] = useState<Form>(() => loadDraft());
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [barShrunk, setBarShrunk] = useState(false);

  const hasContent = formHasContent(form);

  // Autosave: persiste o rascunho a cada mudança (enquanto não foi enviado).
  useEffect(() => {
    if (submitted) return;
    saveDraft(form);
  }, [form, submitted]);

  function reset() {
    setForm(EMPTY);
    setSending(false);
    setError(null);
    setSubmitted(false);
    setBarShrunk(false);
  }

  // Fecha mantendo o rascunho (X, Cancelar, clique fora com o form vazio). O
  // form fica em memória E no localStorage, então reabrir restaura tudo.
  function closeKeep() {
    setOpen(false);
  }

  // Fecha de vez: descarta o rascunho e zera o form (só após enviar, ou no
  // botão "Limpar"). Atrasa o reset pra não piscar o form vazio no fade.
  function closeDiscard() {
    setOpen(false);
    clearDraft();
    setTimeout(reset, 200);
  }

  // "Limpar" sem fechar — recomeça do zero.
  function clearNow() {
    clearDraft();
    reset();
  }

  // Contagem regressiva da tela de obrigado.
  useEffect(() => {
    if (!submitted) return;
    const raf = requestAnimationFrame(() => setBarShrunk(true)); // dispara a transição
    const t = setTimeout(closeDiscard, CLOSE_AFTER_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [submitted]);

  const set = (k: keyof Form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.nome.trim()) {
      setError("Preencha pelo menos o nome.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await supportApi.submit(form);
      clearDraft(); // enviado — o rascunho cumpriu o papel
      setSubmitted(true);
    } catch (e) {
      setError((e as Error).message || "Não consegui enviar agora. Tente de novo.");
      setSending(false);
    }
  }

  const field = (
    label: string,
    key: keyof Form,
    opts?: { type?: "text" | "date"; textarea?: boolean; placeholder?: string },
  ) => (
    <label style={{ display: "block" }}>
      <span style={labelBase}>{label}</span>
      {opts?.textarea ? (
        <textarea
          value={form[key]}
          onChange={(e) => set(key, e.target.value)}
          placeholder={opts?.placeholder}
          rows={3}
          style={{ ...inputBase, resize: "vertical", lineHeight: 1.5 }}
        />
      ) : (
        <input
          type={opts?.type ?? "text"}
          value={form[key]}
          onChange={(e) => set(key, e.target.value)}
          placeholder={opts?.placeholder}
          style={inputBase}
        />
      )}
    </label>
  );

  return (
    <>
      {/* Botão flutuante */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Suporte"
          style={{
            position: "fixed",
            right: 22,
            bottom: 22,
            zIndex: 400,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 18px",
            borderRadius: 999,
            border: "none",
            background: "var(--nd-orange)",
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "var(--wf-font-body)",
            cursor: "pointer",
            boxShadow: "0 6px 20px rgba(255,112,0,0.35)",
          }}
        >
          <LifeBuoy size={18} strokeWidth={2} />
          Suporte
        </button>
      )}

      {/* Modal */}
      {open && (
        <div
          onClick={() => {
            // Clique fora SÓ fecha quando o form está vazio. Com conteúdo, ignora
            // (era o que fazia o time perder o que estava digitando). Mesmo
            // fechando pelo X/Cancelar, o rascunho fica salvo e volta ao reabrir.
            if (!hasContent) closeKeep();
          }}
          className="wf"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5,8,16,0.45)",
            zIndex: 401,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            padding: 22,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 460,
              maxHeight: "calc(100vh - 44px)",
              display: "flex",
              flexDirection: "column",
              background: "var(--wf-paper)",
              borderRadius: 16,
              border: "1px solid var(--wf-ink-5)",
              boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
              overflow: "hidden",
            }}
          >
            {submitted ? (
              // ── Tela de obrigado + contagem regressiva ──
              <div style={{ padding: "36px 28px 0", textAlign: "center" }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    margin: "0 auto 16px",
                    background: "rgba(58,166,87,0.12)",
                    color: "#3aa657",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Check size={28} strokeWidth={2.5} />
                </div>
                <div className="wf-display" style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
                  Obrigado!
                </div>
                <div style={{ fontSize: 13.5, color: "var(--wf-ink-2)", lineHeight: 1.5, maxWidth: 320, margin: "0 auto 24px" }}>
                  Recebemos sua mensagem. A gente analisa e te retorna. Esta janela fecha sozinha.
                </div>
                <div style={{ height: 4, background: "var(--wf-ink-5)", borderRadius: 999, overflow: "hidden", margin: "0 -28px" }}>
                  <div
                    style={{
                      height: "100%",
                      width: barShrunk ? "0%" : "100%",
                      background: "var(--nd-orange)",
                      transition: `width ${CLOSE_AFTER_MS}ms linear`,
                    }}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    padding: "16px 20px",
                    borderBottom: "1px solid var(--wf-ink-5)",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: "rgba(255,112,0,0.12)",
                      color: "var(--nd-orange)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <LifeBuoy size={18} strokeWidth={1.75} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="wf-display" style={{ fontSize: 16, fontWeight: 700 }}>
                      Suporte
                    </div>
                    <div style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
                      Conta o que você precisa que a gente leva pra frente.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={closeKeep}
                    aria-label="Fechar"
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      border: "1px solid var(--wf-ink-5)",
                      background: "var(--wf-paper)",
                      color: "var(--wf-ink-2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <CloseIcon size={15} />
                  </button>
                </div>

                {/* Form */}
                <div style={{ padding: "16px 20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 13 }}>
                  <div style={{ display: "flex", gap: 12 }}>
                    <div style={{ flex: 1 }}>{field("Nome", "nome", { placeholder: "Seu nome" })}</div>
                    <div style={{ width: 150 }}>{field("Data", "data", { type: "date" })}</div>
                  </div>
                  {field("Área", "area", { placeholder: "ex: Tráfego, Social, Comercial…" })}
                  {field("Nova feature", "novaFeature", { placeholder: "O que você gostaria que existisse" })}
                  {field("Cenário", "cenario", { textarea: true, placeholder: "Em que situação isso aparece" })}
                  {field("Processo atual", "processoAtual", { textarea: true, placeholder: "Como você faz isso hoje" })}
                  {field("Ferramentas usadas", "ferramentas", { placeholder: "ex: Planilha, n8n, ChatGPT…" })}
                  {error && <div style={{ fontSize: 12.5, color: "var(--nd-orange-pun)" }}>{error}</div>}
                </div>

                {/* Footer */}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "13px 20px",
                    borderTop: "1px solid var(--wf-ink-5)",
                    background: "var(--wf-bg)",
                    flexShrink: 0,
                  }}
                >
                  <button type="button" className="wf-btn wf-btn--ghost" onClick={closeKeep}>
                    Fechar
                  </button>
                  {/* Reforço visual de que nada se perde + atalho pra recomeçar. */}
                  {hasContent && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 11.5,
                        color: "var(--wf-ink-3)",
                      }}
                    >
                      <Check size={13} strokeWidth={2.5} style={{ color: "#3aa657" }} />
                      Rascunho salvo
                      <button
                        type="button"
                        onClick={clearNow}
                        style={{
                          border: "none",
                          background: "none",
                          padding: 0,
                          marginLeft: 4,
                          color: "var(--wf-ink-3)",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: 11.5,
                          fontFamily: "inherit",
                        }}
                      >
                        Limpar
                      </button>
                    </div>
                  )}
                  <div style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="wf-btn wf-btn--primary"
                    onClick={submit}
                    disabled={sending}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <Send size={14} strokeWidth={2} />
                    {sending ? "Enviando…" : "Enviar"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
